import { ChangeEvent, SyntheticEvent, useContext, useState } from "react";
import {
  Autocomplete,
  Button,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { TemplateListContext } from "./AddOrEditPage";
import AddIcon from "@mui/icons-material/Add";
import CustomTableBody from "../../../utilis/component/CustomTableBody";
import { initialField, LabListType, TemplateFieldType } from "./types";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import { getAllLabs } from "./services";
import { useQuery } from "react-query";
function TemplateList() {
  const { edit, fullList, setEdit, setFullList } =
    useContext(TemplateListContext);

  const [selected, setSelected] = useState(-1);
  const [fieldError, setFieldError] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedName, setSelectedName] = useState<LabListType>({
    id: 0,
    code: "",
    ltemplateName: "",
    ltemplateRange: 0,
    ltemplateUnit: "",
  });

  const { data: labList, isLoading: labListLoading } = useQuery(
    ["observationlablist", edit.fieldName],
    async () => await getAllLabs(searchText)
  );

  const handleAddNew = () => {
    const newData: TemplateFieldType = initialField;
    setEdit(newData);
    setFullList((prev) => [newData, ...prev]);
    setSelected(0);
    setSelectedName({
      id: 0,
      code: "",
      ltemplateName: "",
      ltemplateRange: 0,
      ltemplateUnit: "",
    });
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const field = e.target.dataset.field;
    const value = e.target.value;
    edit &&
      setEdit((prev) => ({ ...prev, [field as keyof typeof prev]: value }));
  };

  const handleSave = () => {
    if (edit.fieldName && edit.fieldRange && edit.fieldUnit) {
      const update = [...fullList];
      update[selected] = { ...edit, fieldName: selectedName.ltemplateName };
      setFullList(update);
      setSelected(-1);
    } else {
      setFieldError(true);
    }
  };

  const handleClear = () => {
    setSelected(-1);
    setFieldError(false);
    if (edit.fieldName == "" && edit.fieldRange == "" && edit.fieldUnit == "") {
      const update = [...fullList];
      update.shift();
      setFullList(update);
    }
  };

  const handleEdit = (index: number, fields: TemplateFieldType) => {
    setSelected(index);
    setEdit(fields);
    setSelectedName({
      code: fields.cptCode as string,
      id: fields.loincId as number,
      ltemplateName: fields.fieldName as string,
      ltemplateRange: fields.fieldRange,
      ltemplateUnit: fields.fieldUnit as string,
    });
  };

  const handleChangeName = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setEdit((prev) => ({ ...prev, fieldName: e.target.value }));
  };

  const handleBlur = () => {
    setSelectedName({
      code: "",
      id: 0,
      ltemplateName: edit.fieldName,
      ltemplateRange: "",
      ltemplateUnit: "",
    });
  };

  const handleSelectName = (
    _: SyntheticEvent<Element, Event>,
    option: LabListType | null
  ) => {
    if (option) {
      setSelectedName(option);
      setEdit((prev) => ({
        ...prev,
        cptId:option.id,
        fieldName:option.ltemplateName,
        fieldRange: `${option.ltemplateRange}`,
        fieldUnit: option.ltemplateUnit as string,
      }));
    }
  };

  return (
    <div className="p-2">
      <div className="border-2">
        <div className="p-2 flex justify-between items-center">
          <Typography fontWeight={"500"} color={"primary"}>
            Add template details
          </Typography>
          <Button
            disabled={selected != -1}
            endIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            add value
          </Button>
        </div>
        <Divider />
        <Table size="small">
          <TableHead>
            <TableRow>
              {headings.map((title, index) => (
                <TableCell key={`${title}${index}`}>{title}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <CustomTableBody
            alert={"No data"}
            dataLoading={false}
            isData={Boolean(fullList.length)}
            totalSpan={{ row: 5, col: headings.length }}
          >
            {fullList.map((item, index) =>
              selected == index ? (
                <TableRow>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell width={250}>
                    <TextField
                      variant="standard"
                      label="Code"
                      value={edit?.loincCode}
                      inputProps={{ "data-field": "loincCode" }}
                      onChange={handleTextChange}
                    />
                  </TableCell>
                  <TableCell width={370}>
                    <Autocomplete
                      fullWidth
                      value={selectedName}
                      options={labList?.response || []}
                      loading={labListLoading}
                      onBlur={handleBlur}
                      onChange={handleSelectName}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          label="Name"
                          onChange={handleChangeName}
                        />
                      )}
                      getOptionLabel={(option) => option.ltemplateName}
                      renderOption={(params, option) => (
                        <ListItem {...params}>
                          <ListItemIcon>{option.code}</ListItemIcon>{" "}
                          {option.ltemplateName}
                        </ListItem>
                      )}
                    />
                  </TableCell>
                  <TableCell width={250}>
                    <TextField
                      variant="standard"
                      label="Unit"
                      value={edit?.fieldUnit}
                      inputProps={{ "data-field": "fieldUnit" }}
                      onChange={handleTextChange}
                      error={Boolean(!edit?.fieldUnit && fieldError)}
                    />
                  </TableCell>
                  <TableCell width={250}>
                    <TextField
                      variant="standard"
                      label="Range"
                      value={edit?.fieldRange}
                      inputProps={{ "data-field": "fieldRange" }}
                      onChange={handleTextChange}
                      error={Boolean(!edit?.fieldRange && fieldError)}
                    />
                  </TableCell>
                  <TableCell width={250}>
                    <IconButton onClick={handleSave} color="success">
                      <DoneIcon />
                    </IconButton>
                    <IconButton onClick={handleClear} color="error">
                      <CloseIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.loincCode}</TableCell>
                  <TableCell>{item.fieldName}</TableCell>
                  <TableCell>{item.fieldUnit}</TableCell>
                  <TableCell>{item.fieldRange}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => {
                        handleEdit(index, item);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            )}
          </CustomTableBody>
        </Table>
      </div>
    </div>
  );
}

export default TemplateList;
const headings = ["SL NO", "Code", "NAME", "UNIT", "RANGE", "ACTIONS"];
